/**
 * The order-intent wire vocabulary — ROADMAP 4.9, ARCHITECTURE.md §5.1.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE COMMAND SURFACE
 *
 * §5.1: every order, from every origin — chain click, keyboard, strategy
 * deployment, guardian stop, scheduler, kill switch — constructs the SAME
 * internal `OrderIntent` before any broker is touched. This file is the shape
 * a BROWSER uses to ask for one. A webhook or an HTTP adapter added later is
 * another thin transport onto the same vocabulary, not a parallel order path.
 *
 * It is deliberately a REQUEST, not a command: nothing here names a broker,
 * an order id, or a state. The client asks; the server decides.
 *
 * WHY THE CLIENT SUPPLIES A REQUEST ID BUT NOT AN IDEMPOTENCY KEY
 *
 * §5.7 is explicit that `idemKey` is generated SERVER-SIDE and never supplied
 * by the client — Stoxxo pushes that onto the caller, which is why a replayed
 * webhook there can double-fire.
 *
 * But a key with nothing stable in it cannot dedupe a retry, and a double-
 * clicked button or a resent frame after a reconnect must resolve to ONE
 * intent. So the client supplies `clientRequestId`: one value per user
 * ACTION, reused on every retry of that same action. The server derives the
 * idempotency key from it together with the authenticated user id, so a
 * client can neither choose another user's key nor collide with one.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/** Limits enforced at the boundary, before anything is written. */
export const INTENT_LIMITS = {
    /**
     * The most legs one ENTRY may carry.
     *
     * APPLIES TO LEGS A CLIENT COMPOSED. It must never be applied to a CLOSING
     * order — see `maxClosingLegs`.
     *
     * ─────────────────────────────────────────────────────────────────────────
     * RAISED FROM 10 TO 40 ON 2026-11-03, AND THE OLD REASONING WENT WITH IT
     *
     * This used to read "a basket bigger than this is a mistake or an attack,
     * not a spread". That sentence was already known to be unreliable — it is
     * the exact claim `maxClosingLegs` below had to carve an exception out of
     * after EXIT ALL silently refused eleven legs — and it is false for the
     * other case too: a thirty-leg strategy a trader built, named and saved is
     * neither a mistake nor an attack.
     *
     * Raised at the owner's instruction, globally rather than as a second cap
     * for saved strategies, so ANY composed entry may now carry forty legs.
     * That is the cost and it is stated here rather than discovered: the
     * browser is no longer bounded at ten.
     *
     * WHAT STILL BOUNDS IT. Forty is not unlimited, and the things that make a
     * large basket survivable are elsewhere and unchanged: gate 9 PACES a
     * basket against the account's order-rate budget rather than firing it at
     * once (9.17), `maxQty` bounds each leg, and every leg is gated and priced
     * individually before any of them reaches the wire.
     * ─────────────────────────────────────────────────────────────────────────
     */
    maxLegs: 40,
    /**
     * The same cap, for an exit the SERVER planned — ROADMAP 5.12.
     *
     * ─────────────────────────────────────────────────────────────────────────
     * HOW MANY LEGS AN EXIT NEEDS IS NOT THE TRADER'S CHOICE
     *
     * `maxLegs` is a statement about an entry: nobody deliberately opens an
     * eleven-leg spread, so eleven means a bug or an attack. The same sentence
     * is simply false about getting out. The number of legs a close needs is
     * however many are open, this software created them one click at a time,
     * and "Exit all" is the gesture that means all of them.
     *
     * Measured on 2026-10-26: eleven chain clicks on one strike, and EXIT ALL
     * did nothing a trader could see — the intent carried eleven legs, `maxLegs`
     * refused it, and the refusal rendered below the fold. The trader asked why
     * the button was not working. It was working; the limit was wrong.
     *
     * Still bounded rather than unlimited, because an unbounded leg count is an
     * unbounded burst at the broker. A hundred is far beyond any real book and
     * still a number a rate limiter can survive. It is not a risk gate: every
     * leg here was planned by `planExitByLeg` from our own record, and the
     * worst case it permits is getting completely flat.
     * ─────────────────────────────────────────────────────────────────────────
     */
    maxClosingLegs: 100,
    /*
     * Per leg, and a WIRE SANITY BOUND rather than a risk limit — it stops a
     * malformed request, not a large trade. The exchange's own freeze limit is
     * the exchange's to enforce; that gate was removed on 2026-10-28.
     *
     * Raised from 10,000 on 2026-10-30 to match the size box, which the owner
     * set to 10,000,000. Shipping a field that can build an order the server
     * refuses by name is the "UI promises what the server refuses" problem
     * this codebase keeps removing, and leaving the two numbers disagreeing
     * was a mistake.
     */
    maxLots: 10_000_000,
    /**
     * The bound that actually matters, in UNITS.
     *
     * `lots` alone is meaningless as a safety bound because a lot is not a
     * fixed size: production carries lot sizes from 1 to 71,475, so
     * 10,000,000 lots is 200 million units on SENSEX and 714 BILLION on the
     * largest contract — and `BrokerOrder.qty` is a Postgres `Int`, which
     * stops at 2,147,483,647. The second case does not refuse, it OVERFLOWS.
     *
     * So the ceiling is on the product, with headroom under int4, and it is
     * checked where the lot size is known rather than guessed at the wire.
     */
    maxQty: 2_000_000_000,
    maxClientRequestIdLength: 64,
};
/** Engine -> gateway. One channel for every user; the gateway filters. */
export const EXECUTION_EVENT_CHANNEL = 'apna:execution:events';
/**
 * Server -> engine: "an intent is waiting".
 *
 * An OPTIMISATION, never the queue. The intent is already durable in
 * Postgres by the time this is published, and the engine also sweeps for
 * unclaimed intents on a timer — so a dropped notification costs latency and
 * nothing else. Redis pub/sub has no delivery guarantee, and an order is not
 * something to put on a transport that can silently lose it.
 */
export const INTENT_WAKE_CHANNEL = 'apna:execution:wake';
/**
 * "Stop releasing this intent's remaining slices" — ROADMAP 5.13.
 *
 * A sliced leg spends seconds on the wire, and a trader who cancels during
 * that window must not have the rest of it sent. The slices ALREADY at the
 * broker are cancelled the durable way, through `BrokerOrder.cancelRequestedAt`
 * and the cancel sweeper; this channel only stops the ones that do not exist
 * yet.
 *
 * DELIBERATELY NOT DURABLE. The thing it stops lives in one engine process's
 * memory, so a lost message costs nothing a restart does not already fix: a
 * process that died mid-release has no unsent slices left to stop.
 */
export const INTENT_CANCEL_CHANNEL = 'apna:execution:cancel';
/**
 * Published by the API after a catalogue sync lands; the engine reloads its
 * instrument index on it — ROADMAP 3.x, fixed 2026-10-14.
 *
 * The engine keeps ONE read-only projection of the catalogue, built with
 * `instrumentIndex.load()`. That ran exactly once, at boot, despite the
 * module's own docstring saying it was "refreshed when the sync runs".
 *
 * On the production box the engine booted at 19:42 while the catalogue was
 * still empty — the XTS master returns nothing after hours, and the sync's
 * plausibility floor correctly refused to write zero rows. The 08:15 sync then
 * filled the database with 140,698 instruments and nothing told the engine. It
 * ran all of the next session with an index of ZERO: every token resolved to
 * no underlying, so no shard owned it, so nothing was ever subscribed, so the
 * socket received no frames and the silence watchdog rebuilt it every eight
 * seconds — and every order was refused for having no price.
 *
 * The failure is not limited to a cold deploy. A weekly expiry lists new
 * strikes at 08:15, and without this the engine is blind to all of them until
 * someone restarts it.
 *
 * The payload is advisory and ignored; the signal is the message itself. A
 * missed publish costs a stale index until the next one, which is the same
 * exposure the intent wake channel accepts for the same reason.
 */
export const CATALOGUE_SYNCED_CHANNEL = 'apna:catalogue:synced';
//# sourceMappingURL=orderIntentWire.js.map